// src/components/settings/SettingsModal.tsx
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSessionUser } from "../../utils/auth/sessionUser";

import DetailDrawer from "../shared/DetailDrawer";
import { useSettings } from "./SettingsContext";
import { isSettingsRailHeading, visibleSettingsTabs } from "./settingsTabs";
import { fetchClinicCompany } from "../../api/admin/companyApi";
import { Company } from "../../types/admin/CompanyTypes";
import { User } from "../../types/admin/UserTypes";

/** One key, so the drawer and any later writer agree on the cache entry. */
export const CLINIC_COMPANY_QUERY_KEY = ["clinic-company"] as const;

/**
 * the settings drawer.
 */
const SettingsModal: React.FC = () => {
	const { open, activeTab, openSettings, closeSettings, consumeNavigationHandoff } = useSettings();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { data: company, isError } = useQuery<Company>({
		queryKey: CLINIC_COMPANY_QUERY_KEY,
		queryFn: fetchClinicCompany,
		enabled: open,
	});

	// Same write-back shape InvoicingSettingsTab's mutation uses: seed the cache rather than
	// invalidate, so a tab that just saved does not re-fetch what it already holds.
	const onCompanyUpdated = useCallback(
		(updated: Company) => {
			queryClient.setQueryData(CLINIC_COMPANY_QUERY_KEY, updated);
		},
		[queryClient],
	);

	// the drawer closes on navigation. 
	const seenFirstPathname = useRef(false);
	useEffect(() => {
		if (!seenFirstPathname.current) {
			seenFirstPathname.current = true;
			return;
		}
		if (consumeNavigationHandoff()) return;
		closeSettings();
	}, [location.pathname, closeSettings, consumeNavigationHandoff]);

	const user: User = useMemo(() => getSessionUser(), []);


	const tabs = useMemo(() => {
		if (!company) return [];
		const ctx = { company, onCompanyUpdated, user };
		return visibleSettingsTabs().map((tab) => ({
			key: tab.key,
			label: tab.label,
			children: isSettingsRailHeading(tab.key) ? undefined : tab.render(ctx),
		}));
	}, [company, onCompanyUpdated, user]);

	return (
		<DetailDrawer
			open={open}
			onClose={closeSettings}
			width="80vw"
			title="Settings"
			tabLayout="sidebar"
			tabs={tabs}
			activeTab={activeTab}
			onTabChange={openSettings}
			loading={!company && !isError}
			loadingMessage="Loading company data…"
			errorMessages={isError ? ["Failed to load company data."] : []}
		/>
	);
};

export default SettingsModal;
